// pages/admin/AdminDashboard.jsx
import { Link } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { db } from '../../firebase';
import Navbar from '../../components/Navbar';
import {
  collection,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  orderBy,
} from 'firebase/firestore';

export default function AdminDashboard() {
  const [users, setUsers] = useState([]);
  const [sortBy, setSortBy] = useState('chronos');

  useEffect(() => {
    const fetchAllUsers = async () => {
      const snapshot = await getDocs(collection(db, 'users'));
      const allUsers = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setUsers(allUsers);
    };
    fetchAllUsers();
  }, []);

  const sortedUsers = [...users].sort((a, b) => {
    if (sortBy === 'name') return (a.name || '').localeCompare(b.name || '');
    if (sortBy === 'chronos') return (b.chronos || 0) - (a.chronos || 0);
    if (sortBy === 'streak') return (b.activeDates?.length || 0) - (a.activeDates?.length || 0);
    if (sortBy === 'date') return new Date(a.createdAt || 0) - new Date(b.createdAt || 0);
    return 0;
  });

  const updateStatus = async (id, status) => {
    await updateDoc(doc(db, 'users', id), { isActive: status });
    setUsers(users.map(u => u.id === id ? { ...u, isActive: status } : u));
  };

  const activateProPlan = async (id, days) => {
    const expiryDate = new Date();
    expiryDate.setDate(expiryDate.getDate() + days);
    await updateDoc(doc(db, 'users', id), {
      accessType: 'pro',
      accessExpiry: expiryDate.toISOString()
    });
    setUsers(users.map(u => u.id === id ? { ...u, accessType: 'pro', accessExpiry: expiryDate.toISOString() } : u));
  };

  const makeDemo = async (id) => {
    await updateDoc(doc(db, 'users', id), { accessType: 'demo', accessExpiry: null });
    setUsers(users.map(u => u.id === id ? { ...u, accessType: 'demo', accessExpiry: null } : u));
  };

  const resetChronos = async (uid) => {
    await updateDoc(doc(db, 'users', uid), { chronos: 0 });
    setUsers(users.map((u) => (u.id === uid ? { ...u, chronos: 0 } : u)));
  };

  const rewardChronos = async (uid, bonus) => {
    const userRef = doc(db, 'users', uid);
    const userSnap = await getDoc(userRef);
    const current = userSnap.data().chronos || 0;
    await updateDoc(userRef, { chronos: current + bonus });
    setUsers(users.map((u) => (u.id === uid ? { ...u, chronos: current + bonus } : u)));
  };

  const cleanFutureDates = async (uid) => {
    const today = new Date().toISOString().split('T')[0];
    const userRef = doc(db, 'users', uid);
    const snap = await getDoc(userRef);
    const active = (snap.data().activeDates || []).filter((d) => d <= today);
    await updateDoc(userRef, { activeDates: active });
    setUsers(users.map((u) => u.id === uid ? { ...u, activeDates: active } : u));
  };

  const deleteUser = async (uid) => {
    await deleteDoc(doc(db, 'users', uid));
    setUsers(users.filter((u) => u.id !== uid));
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-purple-800">🛠 Be Brainy Admin Panel</h1>
            <p className="text-sm text-gray-600 mt-1">Manage users efficiently and professionally</p>
          </div>
          <Link
            to="/admin/brain-test"
            className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
          >
            🚀 Start Brain Test
          </Link>
        </div>

        <div className="mb-4 flex gap-4 items-center">
          <button
  onClick={async () => {
    try {
      const topThree = [...users]
        .sort((a, b) => (b.chronos || 0) - (a.chronos || 0))
        .slice(0, 3);

await setDoc(doc(db, 'highlights', 'topPlayers'), {
  timestamp: new Date().toISOString(),
  players: topThree,
});


      const batch = users.map((u) =>
        updateDoc(doc(db, 'users', u.id), { chronos: 0 })
      );

      await Promise.all(batch);

      setUsers(users.map((u) => ({ ...u, chronos: 0 })));
      alert('✅ All chronos have been reset and top 3 saved!');
    } catch (error) {
      console.error('❌ Reset failed:', error.message);
      alert('❌ Reset failed: ' + error.message);
    }
  }}
  className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-sm"
>
  Reset All Chronos
</button>

<button
  onClick={async () => {
    try {
      const leaderboardRef = collection(db, 'leaderboard_master');
      const snapshot = await getDocs(leaderboardRef);
      const allEntries = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      }));

      // Sort and get top 3
      const topThree = [...allEntries]
        .sort((a, b) => (b.score || 0) - (a.score || 0))
        .slice(0, 3);

      // Save top 3 before reset
      await setDoc(doc(db, 'highlights', 'topLeaderboard'), {
        timestamp: new Date().toISOString(),
        players: topThree,
      });

      // Reset all scores to 0
      const resetBatch = allEntries.map((entry) =>
        updateDoc(doc(db, 'leaderboard_master', entry.id), { score: 0 })
      );
      await Promise.all(resetBatch);

      alert('✅ Leaderboard archived and reset.');
    } catch (error) {
      console.error('❌ Leaderboard reset failed:', error.message);
      alert('❌ Leaderboard reset failed: ' + error.message);
    }
  }}
  className="bg-pink-500 hover:bg-pink-600 text-white px-3 py-1 rounded text-sm"
>
  Reset & Archive Leaderboard
</button>



          <h2 className="text-lg font-semibold">Sort By:</h2>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1"
          >
            <option value="chronos">Chronos</option>
            <option value="name">Name</option>
            <option value="streak">Streak</option>
            <option value="date">Date</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full bg-white rounded shadow table-auto">
            <thead className="bg-purple-100 text-purple-800">
              <tr>
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Email</th>
                <th className="px-4 py-2 text-left">Referrer</th>
                <th className="px-4 py-2 text-center">Chronos</th>
                <th className="px-4 py-2 text-center">Streak</th>
                <th className="px-4 py-2 text-center">Status</th>
                <th className="px-4 py-2 text-center">Access</th>
                <th className="px-4 py-2 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedUsers.map((user) => (
                <tr key={user.id} className="border-t text-center even:bg-gray-50 hover:bg-yellow-50">
                  <td className="px-4 py-2 text-left">{user.name || '—'}</td>
                  <td className="px-4 py-2 text-left">{user.email}</td>
                  <td className="px-4 py-2 text-left">{user.referrerName || '—'}</td>
                  <td className="px-4 py-2">{user.chronos ?? 0}</td>
                  <td className="px-4 py-2">{user.activeDates?.length ?? 0}</td>
                  <td className="px-4 py-2">{user.isActive ? '✅ Active' : '🚫 Inactive'}</td>
                  <td className="px-4 py-2 capitalize">{user.accessType || 'demo'}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1 justify-center">
                      <button
                        onClick={() => window.open(`/admin/player-report/${user.id}`, '_blank')}
                        className="bg-orange-400 hover:bg-orange-500 text-white text-xs px-2 py-1 rounded"
                      >Report</button>
                      <button
                        onClick={() => resetChronos(user.id)}
                        className="bg-yellow-300 hover:bg-yellow-400 text-xs px-2 py-1 rounded"
                      >Reset</button>
                      <button
                        onClick={() => rewardChronos(user.id, 25)}
                        className="bg-green-300 hover:bg-green-400 text-xs px-2 py-1 rounded"
                      >+25</button>
                      <button
                        onClick={() => cleanFutureDates(user.id)}
                        className="bg-blue-300 hover:bg-blue-400 text-xs px-2 py-1 rounded"
                      >Clean</button>
                      <button
                        onClick={() => updateStatus(user.id, !user.isActive)}
                        className={`text-xs px-2 py-1 rounded ${user.isActive ? 'bg-red-400 hover:bg-red-500 text-white' : 'bg-gray-300 hover:bg-gray-400'}`}
                      >{user.isActive ? 'Deactivate' : 'Activate'}</button>
                      <button
                        onClick={() => activateProPlan(user.id, 7)}
                        className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1 rounded"
                      >7d Pro</button>
                      <button
                        onClick={() => activateProPlan(user.id, 30)}
                        className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1 rounded"
                      >30d Pro</button>
                      <button
                        onClick={() => activateProPlan(user.id, 180)}
                        className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1 rounded"
                      >6mo Pro</button>
                      <button
                        onClick={() => activateProPlan(user.id, 365)}
                        className="bg-purple-500 hover:bg-purple-600 text-white text-xs px-2 py-1 rounded"
                      >1yr Pro</button>
                      <button
                        onClick={() => makeDemo(user.id)}
                        className="bg-indigo-500 hover:bg-indigo-600 text-white text-xs px-2 py-1 rounded"
                      >Make Demo</button>
                      <button
                        onClick={() => deleteUser(user.id)}
                        className="bg-red-300 hover:bg-red-400 text-white text-xs px-2 py-1 rounded"
                      >Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}
